/**
 * Invoice Utility Functions
 * Handles invoice number generation, formatting, and calculations
 */

/**
 * Generate invoice number
 * Format: INV-YYYYMMDD-XXXXX (e.g., INV-20240115-00001)
 * @param {number} sequenceNumber - Sequence number for the day
 * @returns {string} Invoice number
 */
export const generateInvoiceNumber = (sequenceNumber = 1) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const sequence = String(sequenceNumber).padStart(5, '0');
  
  return `INV-${year}${month}${day}-${sequence}`;
};

/**
 * Calculate GST (Goods and Services Tax) for India
 * @param {number} amount - Base amount
 * @param {number} gstRate - GST rate (default: 18%)
 * @returns {Object} GST breakdown
 */
export const calculateGST = (amount, gstRate = 18) => {
  const cgst = (amount * gstRate) / (2 * 100); // Central GST (half of total GST)
  const sgst = (amount * gstRate) / (2 * 100); // State GST (half of total GST)
  const igst = 0; // Integrated GST (for inter-state, currently 0)
  const totalGST = cgst + sgst + igst;
  const totalWithGST = amount + totalGST;

  return {
    baseAmount: amount,
    cgst,
    sgst,
    igst,
    totalGST,
    gstRate,
    totalWithGST
  };
};

/**
 * Format invoice date
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date
 */
export const formatInvoiceDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format invoice number for display
 * @param {string} invoiceNumber - Invoice number
 * @returns {string} Formatted invoice number
 */
export const formatInvoiceNumber = (invoiceNumber) => {
  return invoiceNumber || 'N/A';
};

/**
 * Calculate invoice totals
 * @param {Array} items - Invoice items
 * @param {number} deliveryCharge - Delivery charge
 * @param {number} platformFee - Platform fee
 * @param {number} discount - Discount amount
 * @param {boolean} includeGST - Whether to include GST
 * @param {number} gstRate - GST rate
 * @returns {Object} Invoice totals
 */
export const calculateInvoiceTotals = (items, deliveryCharge = 0, platformFee = 0, discount = 0, includeGST = true, gstRate = 18, luckyDrawAmount = 0) => {
  const subtotal = items.reduce((sum, item) => {
    const itemPrice = item.discountPrice || item.price;
    return sum + (itemPrice * (item.quantity || 1));
  }, 0);

  const totalBeforeGST = subtotal + deliveryCharge + platformFee - discount + (luckyDrawAmount || 0);

  let gst = { cgst: 0, sgst: 0, igst: 0, totalGST: 0 };
  let grandTotal = totalBeforeGST;

  if (includeGST) {
    gst = calculateGST(totalBeforeGST, gstRate);
    grandTotal = gst.totalWithGST;
  }

  return {
    subtotal,
    deliveryCharge,
    platformFee,
    discount,
    luckyDrawAmount: luckyDrawAmount || 0,
    totalBeforeGST,
    gst,
    grandTotal,
    itemCount: items.reduce((sum, item) => sum + (item.quantity || 0), 0)
  };
};

/**
 * Validate invoice data
 * @param {Object} invoiceData - Invoice data
 * @returns {Object} Validation result
 */
export const validateInvoiceData = (invoiceData) => {
  const errors = [];

  if (!invoiceData.orderId) {
    errors.push('Order ID is required');
  }

  if (!invoiceData.items || invoiceData.items.length === 0) {
    errors.push('At least one item is required');
  }

  if (!invoiceData.customer) {
    errors.push('Customer information is required');
  }

  if (!invoiceData.billingAddress) {
    errors.push('Billing address is required');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get invoice status badge color
 * @param {string} status - Invoice status
 * @returns {Object} Badge configuration
 */
export const getInvoiceStatusBadge = (status) => {
  const statusConfig = {
    generated: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Generated' },
    paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
    void: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Void' }
  };

  return statusConfig[status?.toLowerCase()] || statusConfig.pending;
};

export default {
  generateInvoiceNumber,
  calculateGST,
  formatInvoiceDate,
  formatInvoiceNumber,
  calculateInvoiceTotals,
  validateInvoiceData,
  getInvoiceStatusBadge
};


